//-- Copyright (c) 2021 TMT Insights Group, LLC
import React, { useContext } from 'react';
import styled from '@emotion/styled';
import { Button, Container, Grid } from '@mui/material';

import { ConfigContext } from '@/contextAPI/configContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { clearFilters } from '@/redux/slices/dataTableSlice';
import {
  selectCurrentTableKey,
  selectCurrentTableLabel,
  selectDataTableIsMultiSort,
  selectDataTableLoading,
  selectDataTableResults,
  selectDataTableType,
  selectIsCurrentlyGridView,
  selectIsFiltered,
  selectUserColumns
} from '@/redux/selectors/dataTableSelectors';

import { COLUMNS } from '@/_utils/columns';
import { convertStylesToStyled } from '@/_utils/utils';

import { ColumnSelector, RefreshTable, SaveSearch } from '@/components/_common/Table';
import { FilterProps } from '@/components/_common/Filters/Filter';
import PolarisIcon from '@/components/_common/PolarisIcon';
import ResultsLabel from '@/components/_common/ResultsLabel';
import ExportReport from '@/components/_common/Table/ExportReport';
import SavedSearches from '@/components/_common/Table/SavedSearches';
import Filters from '@/components/_common/Filters/Filters';
import ToggleListGrid from '@/components/_common/Filters/ToggleListGrid';
import ToggleFullWidth from '@/components/_common/ToggleFullWidth';
import QuerystringController from '@/components/_common/Filters/FiltersQuerystringController';
import AdvanceSortButton from '@/components/_common/Table/AdvanceSort/AdvanceSortButton';
import CalendarFilters from '@/components/_common/Filters/CalendarFilters';

export interface FiltersBarProps {
  filters: FilterProps[];
  visibleFilters: FilterProps[];
  disableSavedSearch?: boolean;
  disableExportReport?: boolean;
  visibleQuickFilters?: FilterProps[];
  anyAllFilters?: FilterProps[];
  openFilterLayoutsModal: () => void;
}

const FiltersBar = ({
  filters,
  visibleFilters,
  visibleQuickFilters,
  anyAllFilters,
  disableSavedSearch = false,
  disableExportReport = false,
  openFilterLayoutsModal
}: FiltersBarProps) => {
  const filtersVersion = useContext(ConfigContext)?.filtersVersion;
  const dispatch = useAppDispatch();
  const isCalendarTable = useAppSelector(selectDataTableType) === 'calendar';
  const results = useAppSelector(selectDataTableResults);
  const isFiltered = useAppSelector(selectIsFiltered);
  const columns = useAppSelector(selectUserColumns);
  const isLoading = useAppSelector(selectDataTableLoading);
  const tableName = useAppSelector(selectCurrentTableKey);
  const isMultiSort = useAppSelector(selectDataTableIsMultiSort);
  if (!tableName) return null;
  const label = useAppSelector(selectCurrentTableLabel);
  const isCurrentlyGridView = useAppSelector(selectIsCurrentlyGridView);

  const onClearFilters = () => dispatch(clearFilters(undefined));

  return (
    <StyledContainer style={{ marginBottom: isCalendarTable ? undefined : 30, marginTop: 20 }}>
      {isCalendarTable ? (
        <CalendarFilters filters={visibleFilters} />
      ) : (
        <>
          <StyledResultsContainerGrid container justifyContent="space-between" alignItems="center" wrap="nowrap">
            <Grid display="flex" alignItems="center" item>
              <ResultsLabel results={isLoading ? undefined : results} text={isFiltered ? `${label} found` : `total ${label}`} />
              {(!filtersVersion || filtersVersion === 1) && isFiltered && (
                <>
                  <StyledClearFiltersButton
                    data-testid="clear-filters-btn"
                    color="primary"
                    size="small"
                    variant="text"
                    onClick={onClearFilters}
                    startIcon={<PolarisIcon icon="close" />}
                    sx={{ mr: 1, ml: 1 }}
                  >
                    Clear Filters
                  </StyledClearFiltersButton>
                  {!disableSavedSearch && <SaveSearch />}
                </>
              )}
            </Grid>
            {!disableExportReport && <ExportReport />}
            {!disableSavedSearch && <SavedSearches />}
          </StyledResultsContainerGrid>
          <StyledFiltersContainerGrid container alignItems="flex-start">
            <FilterWrapper>
              <Filters
                filters={filters}
                visibleFilters={visibleFilters}
                visibleQuickFilters={visibleQuickFilters}
                anyAllFilters={anyAllFilters}
                openFilterLayoutsModal={openFilterLayoutsModal}
              />
            </FilterWrapper>
            <StyledGrid item>
              <ToggleListGrid />
              <RefreshTable />
              {isMultiSort && <AdvanceSortButton />}
              {!isCurrentlyGridView && columns?.length > 0 && (
                <ColumnSelector columns={columns} allColumns={COLUMNS[tableName as keyof typeof COLUMNS]() as any} />
              )}
              <ToggleFullWidth />
            </StyledGrid>
          </StyledFiltersContainerGrid>
        </>
      )}
    </StyledContainer>
  );
};

export default QuerystringController(FiltersBar);

const StyledGrid = styled(Grid)`
  margin-left: auto;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledContainer = styled(Container)`
  ${({ theme }) => convertStylesToStyled(theme.customOverrides?.FiltersBar)}
`;

const StyledResultsContainerGrid = styled(Grid)`
  margin-bottom: ${(props) => props.theme.spacing(1.5)};
  height: 30px;
`;

const StyledClearFiltersButton = styled(Button)`
  margin-left: ${(props) => props.theme.spacing(1)};
  height: 30px;
`;

const StyledFiltersContainerGrid = styled(Grid)`
  min-height: 46px;
  flex-wrap: nowrap;
`;

const FilterWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  row-gap: ${({ theme }) => theme.spacing(1)};
`;
